/**
 * Minimum Quantity Validation Layer for Liquid Ajax Cart
 * 
 * Intercepts cart requests to enforce minimum quantity rules based on
 * the `min` attribute on quantity inputs. Works with all request types
 * (change, update, add) and handles both FormData and JSON bodies.
 */

// Helper function to get quantity value from request body (FormData or object)
function getQuantityFromBody(body, key = 'quantity') {
  if (body instanceof FormData || body instanceof URLSearchParams) {
    return body.has(key) ? parseInt(body.get(key).toString(), 10) : null;
  }
  return body && typeof body[key] !== 'undefined' ? parseInt(body[key], 10) : null;
}

// Helper function to set quantity value in request body (FormData or object)
function setQuantityInBody(body, value, key = 'quantity') {
  if (body instanceof FormData || body instanceof URLSearchParams) {
    body.set(key, value.toString());
  } else if (body && typeof body === 'object') {
    body[key] = value;
  }
}

// Helper function to get line identifier from request body
function getLineIdentifier(body) {
  let lineKey = null;
  let idKey = null;

  if (body instanceof FormData || body instanceof URLSearchParams) {
    if (body.has('line')) {
      lineKey = body.get('line').toString();
    }
    if (body.has('id')) {
      idKey = body.get('id').toString();
    }
  } else if (body && typeof body === 'object') {
    if (body.line != null) {
      lineKey = body.line.toString();
    }
    if (body.id != null) {
      idKey = body.id.toString();
    }
  }

  return { lineKey, idKey };
}

// Helper function to find matching input by line identifier
function findMatchingInput(lineKey, idKey) {
  const inputs = document.querySelectorAll('input[data-ajax-cart-quantity-input]');
  
  for (const input of inputs) {
    const inputAttr = input.getAttribute('data-ajax-cart-quantity-input').trim();
    
    // Match by line number (index)
    if (lineKey && inputAttr === lineKey) {
      return input;
    }
    
    // Match by id (key) - need to check cart state
    if (idKey && inputAttr.length > 3 && inputAttr === idKey) {
      return input;
    }
    
    // Also check if inputAttr is a line number that matches
    if (lineKey && !isNaN(inputAttr) && parseInt(inputAttr, 10) === parseInt(lineKey, 10)) {
      return input;
    }
  }
  
  return null;
}

// Helper function to get total quantity of grouped items (same product, same color)
// Uses liquid-ajax-cart's cart state for accurate quantities
function getGroupedItemsTotal(productId, colorValue, excludeLineKey = null) {
  if (!productId || !colorValue) return 0;
  
  // Wait for liquid-ajax-cart to be initialized
  if (!window.liquidAjaxCart || !window.liquidAjaxCart.cart) {
    console.warn('Liquid Ajax Cart not initialized yet');
    return 0;
  }
  
  const cart = window.liquidAjaxCart.cart;
  if (!cart || !cart.items || !Array.isArray(cart.items)) {
    return 0;
  }
  
  let total = 0;
  const productIdNum = parseInt(productId, 10);
  
  cart.items.forEach((item, index) => {
    // Skip if this is the item being changed (by line number)
    if (excludeLineKey && (index + 1).toString() === excludeLineKey.toString()) {
      return;
    }
    
    // Match product ID
    if (item.product_id === productIdNum) {
      // Get color option value
      const colorOption = item.options_with_values?.find(opt => 
        opt.name && opt.name.toLowerCase() === 'color'
      );
      
      if (colorOption && colorOption.value === colorValue) {
        total += item.quantity || 0;
      }
    }
  });
  
  return total;
}

// Initialize validation when liquid-ajax-cart is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
      // Already initialized
    } else {
      // Wait for init event
      document.addEventListener('liquid-ajax-cart:init', () => {
        console.log('Liquid Ajax Cart initialized, product-level min qty validation ready');
      }, { once: true });
    }
  });
} else {
  // DOM already loaded
  if (window.liquidAjaxCart && window.liquidAjaxCart.cart) {
    // Already initialized
  } else {
    document.addEventListener('liquid-ajax-cart:init', () => {
      console.log('Liquid Ajax Cart initialized, product-level min qty validation ready');
    }, { once: true });
  }
}

// Intercept liquid-ajax-cart requests to enforce min quantity
// This event fires AFTER liquid-ajax-cart reads the input but BEFORE the
// AJAX request is sent — so modifying requestBody here is guaranteed to work.
document.addEventListener('liquid-ajax-cart:request-start', function(event) {
  const requestState = event.detail && event.detail.requestState;
  if (!requestState || !requestState.requestBody) return;

  const body = requestState.requestBody;
  const requestType = requestState.requestType;

  // Only handle change and update requests (add doesn't have existing line items)
  if (requestType !== 'change' && requestType !== 'update') return;

  const { lineKey, idKey } = getLineIdentifier(body);
  if (!lineKey && !idKey) return;

  // Get quantity from request body
  const qty = getQuantityFromBody(body);
  if (qty === null) return; // Invalid quantity

  // Find matching input to read its min attribute
  const input = findMatchingInput(lineKey, idKey);
  if (!input) return;

  // Minimum rules only apply when reducing quantity (or correcting same-line).
  // Strict increases must not be touched — avoids stale reads and double-click feel.
  let cartLineQty = 0;
  if (window.liquidAjaxCart?.cart?.items) {
    if (lineKey) {
      const idx = parseInt(lineKey, 10) - 1;
      if (idx >= 0 && window.liquidAjaxCart.cart.items[idx]) {
        cartLineQty = window.liquidAjaxCart.cart.items[idx].quantity || 0;
      }
    } else if (idKey) {
      const item = window.liquidAjaxCart.cart.items.find((it) => it.key === idKey);
      if (item) cartLineQty = item.quantity || 0;
    }
  }
  if (cartLineQty === 0) {
    cartLineQty = parseInt(input.value, 10) || 0;
  }
  if (qty > cartLineQty) {
    return;
  }

  const wrapper = input.closest('ajax-cart-quantity');
  if (!wrapper) return;

  const minAttr = parseInt(input.getAttribute('min'), 10);
  const productMinQty = parseInt(wrapper.getAttribute('data-product-min-qty'), 10);
  const productId = wrapper.getAttribute('data-product-id');
  const colorValue = wrapper.getAttribute('data-color-value');

  // Check variant-level minimum (only enforce if minAttr > 0)
  if (!isNaN(minAttr) && minAttr > 0 && qty >= 0 && qty < minAttr) {
    setQuantityInBody(body, minAttr);
    input.value = minAttr;

    // Show error message
    if (typeof wrapper.showMinQtyMessage === 'function') {
      wrapper.showMinQtyMessage(minAttr);
    }
    return;
  }

  // Check product-level minimum for grouped items
  if (!isNaN(productMinQty) && productMinQty > 0 && productId && colorValue) {
    // Get current quantity from cart state (more reliable than input value)
    let currentQty = 0;
    if (window.liquidAjaxCart && window.liquidAjaxCart.cart && window.liquidAjaxCart.cart.items) {
      const lineIndex = parseInt(lineKey, 10) - 1;
      if (lineIndex >= 0 && window.liquidAjaxCart.cart.items[lineIndex]) {
        currentQty = window.liquidAjaxCart.cart.items[lineIndex].quantity || 0;
      }
    }
    
    // Fallback to input value if cart state not available
    if (currentQty === 0) {
      currentQty = parseInt(input.value, 10) || 0;
    }
    
    // Calculate total quantity of grouped items after this change
    const otherItemsTotal = getGroupedItemsTotal(productId, colorValue, lineKey);
    const newTotal = otherItemsTotal + qty;

    // Debug logging (remove in production)
    if (window.location.search.includes('debug=cart')) {
      console.log('Product Min Qty Check:', {
        productId,
        colorValue,
        productMinQty,
        currentQty,
        newQty: qty,
        otherItemsTotal,
        newTotal
      });
    }

    // If reducing quantity would violate product-level minimum
    if (qty < currentQty && newTotal < productMinQty) {
      // Calculate minimum allowed quantity for this item
      const minAllowedQty = Math.max(0, productMinQty - otherItemsTotal);
      
      // If we can't meet minimum even with this item at 0, prevent reduction
      if (minAllowedQty > currentQty) {
        // Keep current quantity
        setQuantityInBody(body, currentQty);
        input.value = currentQty;
        
        // Show product-level minimum error
        if (typeof wrapper.showProductMinQtyMessage === 'function') {
          wrapper.showProductMinQtyMessage(productMinQty, newTotal);
        } else {
          // Fallback to standard message
          if (typeof wrapper.showMinQtyMessage === 'function') {
            wrapper.showMinQtyMessage(productMinQty);
          }
        }
        return;
      }
      
      // Enforce minimum allowed quantity
      if (qty < minAllowedQty) {
        setQuantityInBody(body, minAllowedQty);
        input.value = minAllowedQty;
        
        // Show product-level minimum error
        if (typeof wrapper.showProductMinQtyMessage === 'function') {
          wrapper.showProductMinQtyMessage(productMinQty, minAllowedQty + otherItemsTotal);
        } else {
          if (typeof wrapper.showMinQtyMessage === 'function') {
            wrapper.showMinQtyMessage(productMinQty);
          }
        }
        return;
      }
    }
  }
});

// Custom element for ajax-cart-quantity wrapper
if (!customElements.get('ajax-cart-quantity')) {
  customElements.define(
    'ajax-cart-quantity',
    class AjaxCartQuantity extends HTMLElement {
      constructor() {
        super();
        this._errorTimeout = null;
      }

      connectedCallback() {
        // Handle minus button clicks
        this.addEventListener('click', this.onButtonClick.bind(this), true);
        
        // Handle direct input changes
        const input = this.quantityInput;
        if (input) {
          input.addEventListener('change', this.onInputChange.bind(this));
          input.addEventListener('blur', this.onInputBlur.bind(this));
        }
      }

      disconnectedCallback() {
        if (this._errorTimeout) {
          clearTimeout(this._errorTimeout);
        }
      }

      get quantityInput() {
        return this.querySelector('input[data-ajax-cart-quantity-input]');
      }

      get minQty() {
        const input = this.quantityInput;
        if (!input) return 0;
        const min = parseInt(input.getAttribute('min'), 10);
        return isNaN(min) || min < 0 ? 0 : min;
      }

      get productMinQty() {
        const productMin = parseInt(this.getAttribute('data-product-min-qty'), 10);
        return isNaN(productMin) || productMin <= 0 ? null : productMin;
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get colorValue() {
        return this.getAttribute('data-color-value');
      }

      getGroupedItemsTotal(excludeThisInput = false) {
        const productId = this.productId;
        const colorValue = this.colorValue;
        if (!productId || !colorValue) return 0;

        // Use liquid-ajax-cart cart state for accurate quantities
        if (!window.liquidAjaxCart || !window.liquidAjaxCart.cart) {
          return 0;
        }
        
        const cart = window.liquidAjaxCart.cart;
        if (!cart || !cart.items || !Array.isArray(cart.items)) {
          return 0;
        }
        
        let total = 0;
        const productIdNum = parseInt(productId, 10);
        const thisInput = this.quantityInput;
        const thisLineKey = thisInput ? thisInput.getAttribute('data-ajax-cart-quantity-input') : null;
        
        cart.items.forEach((item, index) => {
          // Skip if this is the item being changed
          if (excludeThisInput && thisLineKey && (index + 1).toString() === thisLineKey.toString()) {
            return;
          }
          
          // Match product ID
          if (item.product_id === productIdNum) {
            // Get color option value
            const colorOption = item.options_with_values?.find(opt => 
              opt.name && opt.name.toLowerCase() === 'color'
            );
            
            if (colorOption && colorOption.value === colorValue) {
              total += item.quantity || 0;
            }
          }
        });
        
        return total;
      }

      onButtonClick(event) {
        const minusBtn = event.target.closest('[data-ajax-cart-quantity-minus]');
        const plusBtn = event.target.closest('[data-ajax-cart-quantity-plus]');
        
        if (!minusBtn && !plusBtn) return;

        const input = this.quantityInput;
        const min = this.minQty;
        if (!input) return;

        const currentVal = parseInt(input.value, 10);
        if (isNaN(currentVal)) return;

        // Handle minus button - check both variant and product-level minimums
        if (minusBtn) {
          const newVal = currentVal - 1;
          
          // Check variant-level minimum
          if (newVal > 0 && newVal < min) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
            
            input.value = min;
            this.showMinQtyMessage(min);
            return;
          }

          // Check product-level minimum for grouped items
          const productMin = this.productMinQty;
          if (productMin && this.productId && this.colorValue) {
            const otherItemsTotal = this.getGroupedItemsTotal(true); // Exclude this input
            const newTotal = otherItemsTotal + newVal;
            
            if (newTotal < productMin) {
              event.preventDefault();
              event.stopImmediatePropagation();
              event.stopPropagation();
              
              // Calculate minimum allowed quantity
              const minAllowedQty = Math.max(0, productMin - otherItemsTotal);
              input.value = minAllowedQty;
              
              this.showProductMinQtyMessage(productMin, minAllowedQty + otherItemsTotal);
              return;
            }
          }
        }

        // Handle plus button - ensure we don't go below minimum after increment
        // (This is mainly for edge cases, plus should always increase)
        if (plusBtn && min > 0 && currentVal < min) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
          
          input.value = min;
          this.showMinQtyMessage(min);
          return;
        }
      }

      onInputChange(event) {
        const input = event.target;
        const min = this.minQty;
        const currentVal = parseInt(input.value, 10);

        if (isNaN(currentVal)) return;

        // Check variant-level minimum (only enforce if min > 0)
        if (min > 0 && currentVal >= 0 && currentVal < min) {
          input.value = min;
          this.showMinQtyMessage(min);
          return;
        }

        // Check product-level minimum for grouped items
        const productMin = this.productMinQty;
        if (productMin && this.productId && this.colorValue && currentVal >= 0) {
          const otherItemsTotal = this.getGroupedItemsTotal(true);
          const newTotal = otherItemsTotal + currentVal;
          
          if (newTotal < productMin) {
            const minAllowedQty = Math.max(0, productMin - otherItemsTotal);
            input.value = minAllowedQty;
            this.showProductMinQtyMessage(productMin, minAllowedQty + otherItemsTotal);
          }
        }
      }

      onInputBlur(event) {
        const input = event.target;
        const min = this.minQty;
        const currentVal = parseInt(input.value, 10);

        if (isNaN(currentVal)) return;

        // Check variant-level minimum (only enforce if min > 0)
        if (min > 0 && currentVal >= 0 && currentVal < min) {
          input.value = min;
          this.showMinQtyMessage(min);
          return;
        }

        // Check product-level minimum for grouped items
        const productMin = this.productMinQty;
        if (productMin && this.productId && this.colorValue && currentVal >= 0) {
          const otherItemsTotal = this.getGroupedItemsTotal(true);
          const newTotal = otherItemsTotal + currentVal;
          
          if (newTotal < productMin) {
            const minAllowedQty = Math.max(0, productMin - otherItemsTotal);
            input.value = minAllowedQty;
            this.showProductMinQtyMessage(productMin, minAllowedQty + otherItemsTotal);
          }
        }
      }

      showMinQtyMessage(min) {
        let msg = this.querySelector('.cart-qty__min-error');
        if (!msg) {
          msg = document.createElement('span');
          msg.className = 'cart-qty__min-error';
          this.appendChild(msg);
        }
        msg.textContent = `Minimum ${min} ${min === 1 ? 'unit' : 'units'} required for this size`;
        msg.classList.add('cart-qty__min-error--visible');

        if (this._errorTimeout) {
          clearTimeout(this._errorTimeout);
        }
        this._errorTimeout = setTimeout(() => {
          msg.classList.remove('cart-qty__min-error--visible');
        }, 3000);
      }

      showProductMinQtyMessage(productMin, currentTotal) {
        let msg = this.querySelector('.cart-qty__min-error');
        if (!msg) {
          msg = document.createElement('span');
          msg.className = 'cart-qty__min-error';
          this.appendChild(msg);
        }
        const unitsText = productMin === 1 ? 'unit' : 'units';
        msg.textContent = `Product minimum: ${productMin} ${unitsText} total required (you have ${currentTotal} ${currentTotal === 1 ? 'unit' : 'units'})`;
        msg.classList.add('cart-qty__min-error--visible');

        if (this._errorTimeout) {
          clearTimeout(this._errorTimeout);
        }
        this._errorTimeout = setTimeout(() => {
          msg.classList.remove('cart-qty__min-error--visible');
        }, 4000);
      }
    }
  );
}
